import { Icon, Loading } from "@focus/focusUI";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { OrbitConjunctionsContainer } from "../../containers/OrbitConjunctionsContainer";
import { type IPlotItem } from "../../containers/PlotsContainer";
import { useHashParams } from "../../hooks/useHashParams";
import { fetchWithAuth } from "../../services/helpers/api";
import { useConfig } from "../../services/helpers/hooks";
import { useOrbitDetail } from "../../services/orbits";
import { ContentHeader } from "../ContentHeader";
import { XMLViewer } from "../XMLViewer";
import { OrbitPlotsPanel } from "./OrbitPlotsPanel";
import { OrbitSummaryCard } from "./OrbitSummaryCard";
import { getOrbitNestedValue } from "./orbitUiUtils";

const FileContentViewer = ({ orbit }: { orbit: unknown }) => {
  const inputId = getOrbitNestedValue(orbit, ["parentUserInput", "id"]) as
    | string
    | undefined;

  const [loading, setLoading] = useState(false);
  const [fetchedContent, setFetchedContent] = useState<string | undefined>();

  useEffect(() => {
    if (!inputId) return;

    let mounted = true;

    const fetchContent = async () => {
      setLoading(true);
      setFetchedContent(undefined);

      try {
        const res = await fetchWithAuth(
          `/api/v1/inputs/${encodeURIComponent(inputId)}/content`,
        );

        if (!res.ok) {
          const msg = await res.text().catch(() => "");
          console.warn("Failed to fetch input content", res.status, msg);
          return;
        }

        const text = await res.text();
        if (!mounted) return;
        setFetchedContent(text || undefined);
      } catch (error) {
        console.warn("Error fetching input content", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchContent();

    return () => {
      mounted = false;
    };
  }, [inputId]);

  return (
    <section className="mx-5 flex min-h-0 w-[calc(100%-2.5rem)] flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <header className="shrink-0 border-b border-slate-200 bg-white px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-950">
              Orbit file content
            </h2>
            <p className="mt-1 text-sm font-medium text-slate-500">
              Preview of the orbit file.
            </p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase text-slate-600">
            File viewer
          </span>
        </div>
      </header>
      <div className="min-h-0 flex flex-1 overflow-auto p-5">
        {loading ? (
          <div className="flex h-full min-h-[240px] w-full items-center justify-center">
            <Loading />
          </div>
        ) : fetchedContent ? (
          <XMLViewer content={fetchedContent} />
        ) : (
          <div className="flex h-full min-h-[240px] w-full">
            <div className="flex h-full w-full items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
              <span className="text-center">
                The file content is not available.
              </span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export const OrbitInformation = () => {
  const { getParam, setParam } = useHashParams();
  const navigate = useNavigate();
  const location = useLocation();

  const config = useConfig("orbits");
  const fetchedOrbit = useOrbitDetail(config);
  const orbit = fetchedOrbit?.value;

  const plots: IPlotItem[] = useMemo(() => {
    const orbPlots = orbit?.plots || [];
    const eventPlots = orbit?.event?.plots || [];
    return [...orbPlots, ...eventPlots];
  }, [orbit]);

  const tabConfig = [
    {
      id: "OrbitConjunctions",
      title: "Details",
      show: true,
      render: () => (
        <div className="h-full w-full overflow-hidden">
          <OrbitConjunctionsContainer />
        </div>
      ),
    },
    {
      id: "Content",
      title: "File content",
      show: true,
      render: () => <FileContentViewer orbit={orbit} />,
    },
    {
      id: "Plots",
      title: "Plots",
      show: Boolean(plots.length),
      render: () => (
        <OrbitPlotsPanel orbit={orbit} plots={plots} showSummary={false} />
      ),
    },
  ];

  const visibleTabs = tabConfig.filter((t) => t.show);

  const [selectedTabId, setSelectedTabId] = React.useState<string | undefined>(
    getParam("tab"),
  );

  useEffect(() => {
    if (!visibleTabs.length) {
      navigate(-1);
      return;
    }

    const tabFromHash = getParam("tab");
    const tabExists = visibleTabs.some((t) => t.id === tabFromHash);

    if (tabFromHash && tabExists) {
      setSelectedTabId(tabFromHash);
      return;
    }

    const defaultTab = visibleTabs[0].id;
    setSelectedTabId(defaultTab);
    setParam("tab", defaultTab);
  }, [visibleTabs, getParam, setParam, navigate, location.key]);

  const handleSelectTab = (tab: string) => {
    setSelectedTabId(tab);
    setParam("tab", tab);
  };

  return (
    <div className="foc-content flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <ContentHeader
          renderCustomTitle={() => (
            <button
              className="-ml-1 inline-flex w-fit cursor-pointer appearance-none items-center gap-1 rounded-md border-0 bg-transparent px-1.5 py-1 text-sm font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              onClick={() => navigate(-1)}
              type="button"
            >
              <Icon icon="chevron-left" size={14} />
              Back to Orbit files
            </button>
          )}
          tabs={visibleTabs}
          selectedTab={selectedTabId}
          handleSelectTab={handleSelectTab}
        />
        <div className="shrink-0 p-5 !pt-0">
          <OrbitSummaryCard className="h-auto" orbit={orbit} />
        </div>
        <div className="foc-table-container flex min-h-0 flex-1 overflow-hidden">
          {visibleTabs.map((tab) =>
            selectedTabId === tab.id ? (
              <React.Fragment key={tab.id}>{tab.render()}</React.Fragment>
            ) : null,
          )}
        </div>
      </div>
    </div>
  );
};
